import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { LIMITS } from '@weblite/permission'
import { styled, Typography } from '@mui/material'
import { WVerificationIllustrationIcon } from '@weblite/icons'
import { t } from '$i18n'
import { formatDuration } from '$func/time'
import { Alert } from '$common/input/alert'
import { ErrorButton } from '$common/buttons'
import { VerificationSession } from '$logic/auth'
import { toHumanReadablePhone } from '$func/phone'
import { confirmDeleteAccount } from '$logic/client-user'
import { VerificationCodeInput } from '$common/verification-code-input'
import { useVerifyPhoneForm } from '$comp/verification/use-verify-phone-form'
import { ContentContainer, TextWrapper, IconWrapper } from './shared'
import { nothing } from '$func/utils'

export interface EnterOtpFormApi {
  handleConfirm: () => unknown
}

export const EnterOtpForm = forwardRef(function SubmitOtpFormComponent(
  { sessionId, phone }: VerificationSession,
  ref: ForwardedRef<EnterOtpFormApi>,
) {
  const [token, setToken] = useState('')
  const [deletingAccount, setDeletingAccount] = useState(false)

  const readablePhone = useMemo(
    () => (phone ? toHumanReadablePhone(phone) : '-'),
    [phone],
  )

  const {
    handleCodeChange,
    handleResendCode,
    resendMaxed,
    remainingMillis,
    alertText,
    loading,
  } = useVerifyPhoneForm({
    phone,
    sessionId,
    signInUser: false,
    onVerified: setToken,
    onCloseVerification: nothing,
  })

  // NOTE: We shouldn't delegate handling loading to ErrorButton because confirm can be triggered
  // by submitting form with enter button
  const handleConfirm = useCallback(() => {
    if (!token) {
      void handleResendCode()
      return
    }
    setDeletingAccount(true)
    confirmDeleteAccount(token).finally(() => setDeletingAccount(false))
  }, [token, handleResendCode])

  useImperativeHandle(
    ref,
    () => {
      return { handleConfirm }
    },
    [handleConfirm],
  )

  return (
    <>
      <ContentContainer>
        <IconWrapper>
          <WVerificationIllustrationIcon
            sx={{ width: '100px', height: '100px' }}
          />
        </IconWrapper>
        <TextWrapper>
          <Typography variant="h3" color="neutral1000" textAlign="center">
            {t('settings.deleteAccount.verification.title')}
          </Typography>
          <Typography variant="subtitle3" color="neutral700" textAlign="center">
            {t('settings.deleteAccount.verification.desc')(readablePhone)}
          </Typography>
        </TextWrapper>

        <OtpWrapper>
          <VerificationCodeInput
            timeout={LIMITS.verificationCode.resendInSeconds * 1000}
            resendMaxed={resendMaxed}
            numFields={LIMITS.verificationCode.length}
            onChange={handleCodeChange}
            hasError={Boolean(alertText)}
          />
          <Alert
            variant="error"
            text={alertText}
            sxStyle={{
              display: alertText ? 'static' : 'none',
            }}
          />
        </OtpWrapper>
      </ContentContainer>

      <ErrorButton
        text={
          <Typography variant="subtitle1" color="neutral000">
            {token
              ? t('settings.deleteAccount.verification.confirm')
              : resendMaxed || remainingMillis <= 0
                ? t('signin.verification.resendCode')
                : t('signin.verification.validTillXMore', {
                    x: formatDuration(remainingMillis),
                  })}
          </Typography>
        }
        type="submit"
        fullWidth
        loading={deletingAccount || loading}
        disabled={
          deletingAccount ||
          (!token && (loading || resendMaxed || remainingMillis > 0))
        }
      />
    </>
  )
})

const OtpWrapper = styled('div')`
  width: 100%;
`
